import {
  LayoutTemplate,
  ListOrdered,
  Moon,
  Store,
  Sun,
  Users,
  Warehouse,
  FileText,
  TicketPercent,
  ScrollText,
  Pill,
  GitCompareArrows,
  GraduationCap,
  AlertCircle,
  Search,
  ShoppingBag,
  Briefcase,
  ShoppingCart,
  Database,
  Settings2,
  Receipt,
  HeadphonesIcon,
  XCircle,
  Bell,
  Route,
  SlidersHorizontal,
  ClipboardList,
  RefreshCw,
  Truck,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  SidebarGroup,
  SidebarDivider,
} from "../../components/ui/sidebar";
import { initAnalytics } from "../../lib/analytics";
import { useTheme } from "../../lib/themeContext";
import pillPlusLogo from "../../pillplus-logo.png";
import { useAuth } from "../../lib/authContext";
import { canAccessRoute } from "../../lib/permissions";

export const Sidebar: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const mixpanel = initAnalytics();
  const { accessLevel, userRole, routePermissions } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // Define route groups for defaultOpen logic
  const routeGroups = {
    operations: [
      "/",
      "/orders",
      "/entities",
      "/providers",
      "/missing_attachments",
      "/order_status_lookup",
      "/users",
      "/entity_rules",
      "/roles_permissions",
      "/team",
      "/bulk_actions",
      "/logistics_partners",
    ],
    b2c: [
      "/page_layouts",
      "/coupons",
      "/b2c_products_mapping",
      "/cart_charges",
      "/support_cases",
      "/b2c_products_master",
      "/b2c_products_management",
      "/b2c_pending_orders",
      "/b2c_refunds",
      "/notifications",
    ],
    data: [
      "/meds_mappings",
      "/meds_category",
      "/products_master",
      "/products_stock",
      "/rejected_orders",
      "/non_operational_days",
    ],
    tech: ["/changelogs", "/queues", "/audit_logs"],
  };

  const isInGroup = (group: string) => {
    return routeGroups[group as keyof typeof routeGroups].includes(currentPath);
  };

  const handleThemeChange = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    mixpanel.track("theme_changed", {
      new_theme: newTheme,
      previous_theme: theme,
    });
  };

  // Helper: active link class
  const activeClass = (path: string) =>
    `dark:hover:bg-gray-800 ${
      currentPath === path
        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
        : ""
    }`;

  // Detect provider-level access
  const isProvider =
    !!accessLevel?.access_level?.provider_id &&
    accessLevel.access_level.provider_id.length > 0;

  return (
    <>
      <ShadcnSidebar
        collapsible="icon"
        side="left"
        className="flex flex-col bg-white dark:bg-gray-900 dark:text-white dark:border-gray-700 h-screen"
      >
        {/* ── Scrollable top section ── */}
        <div className="flex-1 overflow-y-auto">
          <SidebarHeader className="dark:border-gray-700">
            <Link to="/">
              <img
                src={pillPlusLogo}
                alt="PillPlus Logo"
                className="h-8 w-auto mx-auto"
              />
            </Link>
          </SidebarHeader>

          <SidebarContent>
            <SidebarMenu>
              {/* ════════════════════════════════════════════
                  PROVIDER VIEW — only Dashboard + Orders
                  ════════════════════════════════════════════ */}
              {isProvider ? (
                <>
                  <SidebarMenuItem
                    title="Dashboard"
                    className="dark:text-gray-300 mb-2"
                  >
                    <Link to="/">
                      <SidebarMenuButton
                        icon={<Store size={20} />}
                        className={activeClass("/")}
                      >
                        Dashboard
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>

                  <SidebarGroup
                    title="Operations"
                    icon={<Briefcase size={20} />}
                    defaultOpen={isInGroup("operations")}
                  >
                    <SidebarMenuItem
                      title="Manage Orders"
                      className="dark:text-gray-300"
                    >
                      <Link to="/orders">
                        <SidebarMenuButton
                          icon={<ListOrdered size={20} />}
                          className={activeClass("/orders")}
                        >
                          Manage Orders
                        </SidebarMenuButton>
                      </Link>
                    </SidebarMenuItem>

                    <SidebarMenuItem
                      title="Logistics Partners"
                      className="dark:text-gray-300"
                    >
                      <Link to="/logistics_partners">
                        <SidebarMenuButton
                          icon={<Truck size={20} />}
                          className={activeClass("/logistics_partners")}
                        >
                          Logistics Partners
                        </SidebarMenuButton>
                      </Link>
                    </SidebarMenuItem>
                  </SidebarGroup>
                </>
              ) : (
                /* ════════════════════════════════════════════
                 RBAC VIEW — show only permitted routes
                 ════════════════════════════════════════════ */
                <>
                  {/* Dashboard */}
                  {canAccessRoute(userRole, "/", routePermissions) && (
                    <SidebarMenuItem
                      title="Dashboard"
                      className="dark:text-gray-300 mb-2"
                    >
                      <Link to="/">
                        <SidebarMenuButton
                          icon={<Store size={20} />}
                          className={activeClass("/")}
                        >
                          Dashboard
                        </SidebarMenuButton>
                      </Link>
                    </SidebarMenuItem>
                  )}

                  {/* ── Operations ── */}
                  {(canAccessRoute(userRole, "/orders", routePermissions) ||
                    canAccessRoute(userRole, "/entities", routePermissions) ||
                    canAccessRoute(userRole, "/providers", routePermissions) ||
                    canAccessRoute(
                      userRole,
                      "/missing_attachments",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/order_status_lookup",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/entity_rules",
                      routePermissions,
                    ) ||
                    canAccessRoute(userRole, "/users", routePermissions) ||
                    canAccessRoute(
                      userRole,
                      "/roles_permissions",
                      routePermissions,
                    ) ||
                    canAccessRoute(userRole, "/team", routePermissions) ||
                    canAccessRoute(
                      userRole,
                      "/bulk_actions",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/logistics_partners",
                      routePermissions,
                    )) && (
                    <SidebarGroup
                      title="Operations"
                      icon={<Briefcase size={20} />}
                      defaultOpen={isInGroup("operations")}
                    >
                      {canAccessRoute(
                        userRole,
                        "/orders",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Manage Orders"
                          className="dark:text-gray-300"
                        >
                          <Link to="/orders">
                            <SidebarMenuButton
                              icon={<ListOrdered size={20} />}
                              className={activeClass("/orders")}
                            >
                              Manage Orders
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/bulk_actions",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Bulk Actions"
                          className="dark:text-gray-300"
                        >
                          <Link to="/bulk_actions">
                            <SidebarMenuButton
                              icon={<SlidersHorizontal size={20} />}
                              className={activeClass("/bulk_actions")}
                            >
                              Bulk Actions
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/entities",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Manage Entities"
                          className="dark:text-gray-300"
                        >
                          <Link to="/entities">
                            <SidebarMenuButton
                              icon={<Warehouse size={20} />}
                              className={activeClass("/entities")}
                            >
                              Manage Entities
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/providers",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Manage Providers"
                          className="dark:text-gray-300"
                        >
                          <Link to="/providers">
                            <SidebarMenuButton
                              icon={<Briefcase size={20} />}
                              className={activeClass("/providers")}
                            >
                              Manage Providers
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/missing_attachments",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Missing Attachments"
                          className="dark:text-gray-300"
                        >
                          <Link to="/missing_attachments">
                            <SidebarMenuButton
                              icon={<AlertCircle size={20} />}
                              className={activeClass("/missing_attachments")}
                            >
                              Missing Attachments
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/order_status_lookup",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Order Status Lookup"
                          className="dark:text-gray-300"
                        >
                          <Link to="/order_status_lookup">
                            <SidebarMenuButton
                              icon={<Search size={20} />}
                              className={activeClass("/order_status_lookup")}
                            >
                              Order Status Lookup
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/entity_rules",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Order Allocation Rules"
                          className="dark:text-gray-300"
                        >
                          <Link to="/entity_rules">
                            <SidebarMenuButton
                              icon={<Route size={20} />}
                              className={activeClass("/entity_rules")}
                            >
                              Order Allocation Rules
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {(canAccessRoute(userRole, "/users", routePermissions) ||
                        canAccessRoute(
                          userRole,
                          "/roles_permissions",
                          routePermissions,
                        )) && (
                        <SidebarMenuItem
                          title="Team & Permissions"
                          className="dark:text-gray-300"
                        >
                          <Link to="/team">
                            <SidebarMenuButton
                              icon={<Users size={20} />}
                              className={activeClass("/team")}
                            >
                              Team &amp; Permissions
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}

                      {canAccessRoute(
                        userRole,
                        "/logistics_partners",
                        routePermissions,
                      ) && (
                        <SidebarMenuItem
                          title="Logistics Partners"
                          className="dark:text-gray-300"
                        >
                          <Link to="/logistics_partners">
                            <SidebarMenuButton
                              icon={<Truck size={20} />}
                              className={activeClass("/logistics_partners")}
                            >
                              Logistics Partners
                            </SidebarMenuButton>
                          </Link>
                        </SidebarMenuItem>
                      )}
                    </SidebarGroup>
                  )}

                  {/* ── B2C ── */}
                  {(canAccessRoute(
                    userRole,
                    "/b2c_pending_orders",
                    routePermissions,
                  ) ||
                    canAccessRoute(
                      userRole,
                      "/b2c_refunds",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/page_layouts",
                      routePermissions,
                    ) ||
                    canAccessRoute(userRole, "/coupons", routePermissions) ||
                    canAccessRoute(
                      userRole,
                      "/b2c_products_mapping",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/cart_charges",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/support_cases",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/b2c_products_master",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/b2c_products_management",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/notifications",
                      routePermissions,
                    )) && (
                    <>
                      <SidebarDivider />
                      <SidebarGroup
                        title="B2C"
                        icon={<ShoppingCart size={20} />}
                        defaultOpen={isInGroup("b2c")}
                      >
                        {canAccessRoute(
                          userRole,
                          "/b2c_pending_orders",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Pending Orders"
                            className="dark:text-gray-300"
                          >
                            <Link to="/b2c_pending_orders">
                              <SidebarMenuButton
                                icon={<ListOrdered size={20} />}
                                className={activeClass("/b2c_pending_orders")}
                              >
                                Pending Orders
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/b2c_refunds",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Refund Processing"
                            className="dark:text-gray-300"
                          >
                            <Link to="/b2c_refunds">
                              <SidebarMenuButton
                                icon={<RefreshCw size={20} />}
                                className={activeClass("/b2c_refunds")}
                              >
                                Refund Processing
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/page_layouts",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Page Layouts"
                            className="dark:text-gray-300"
                          >
                            <Link to="/page_layouts">
                              <SidebarMenuButton
                                icon={<LayoutTemplate size={20} />}
                                className={activeClass("/page_layouts")}
                              >
                                Page Layouts
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/coupons",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Coupons"
                            className="dark:text-gray-300"
                          >
                            <Link to="/coupons">
                              <SidebarMenuButton
                                icon={<TicketPercent size={20} />}
                                className={activeClass("/coupons")}
                              >
                                Coupons
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/b2c_products_mapping",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="B2C Products Mapping"
                            className="dark:text-gray-300"
                          >
                            <Link to="/b2c_products_mapping">
                              <SidebarMenuButton
                                icon={<ShoppingBag size={20} />}
                                className={activeClass("/b2c_products_mapping")}
                              >
                                B2C Products Mapping
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/cart_charges",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Cart Charges"
                            className="dark:text-gray-300"
                          >
                            <Link to="/cart_charges">
                              <SidebarMenuButton
                                icon={<Receipt size={20} />}
                                className={activeClass("/cart_charges")}
                              >
                                Cart Charges
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/support_cases",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Support Cases"
                            className="dark:text-gray-300"
                          >
                            <Link to="/support_cases">
                              <SidebarMenuButton
                                icon={<HeadphonesIcon size={20} />}
                                className={activeClass("/support_cases")}
                              >
                                Support Cases
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/b2c_products_master",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="B2C Products Master"
                            className="dark:text-gray-300"
                          >
                            <Link to="/b2c_products_master">
                              <SidebarMenuButton
                                icon={<GraduationCap size={20} />}
                                className={activeClass("/b2c_products_master")}
                              >
                                B2C Products Master
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/b2c_products_management",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="B2C Products Management"
                            className="dark:text-gray-300"
                          >
                            <Link to="/b2c_products_management">
                              <SidebarMenuButton
                                icon={<SlidersHorizontal size={20} />}
                                className={activeClass(
                                  "/b2c_products_management",
                                )}
                              >
                                Products Management
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/notifications",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Notifications"
                            className="dark:text-gray-300"
                          >
                            <Link to="/notifications">
                              <SidebarMenuButton
                                icon={<Bell size={20} />}
                                className={activeClass("/notifications")}
                              >
                                Notification Requests
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}
                      </SidebarGroup>
                    </>
                  )}

                  {/* ── Data ── */}
                  {(canAccessRoute(
                    userRole,
                    "/meds_mappings",
                    routePermissions,
                  ) ||
                    canAccessRoute(
                      userRole,
                      "/meds_category",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/products_master",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/products_stock",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/rejected_orders",
                      routePermissions,
                    ) ||
                    canAccessRoute(
                      userRole,
                      "/non_operational_days",
                      routePermissions,
                    )) && (
                    <>
                      <SidebarDivider />
                      <SidebarGroup
                        title="Data"
                        icon={<Database size={20} />}
                        defaultOpen={isInGroup("data")}
                      >
                        {canAccessRoute(
                          userRole,
                          "/meds_mappings",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Meds Mappings"
                            className="dark:text-gray-300"
                          >
                            <Link to="/meds_mappings">
                              <SidebarMenuButton
                                icon={<GitCompareArrows size={20} />}
                                className={activeClass("/meds_mappings")}
                              >
                                Meds Mappings
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/meds_category",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Meds Category"
                            className="dark:text-gray-300"
                          >
                            <Link to="/meds_category">
                              <SidebarMenuButton
                                icon={<Pill size={20} />}
                                className={activeClass("/meds_category")}
                              >
                                Meds Category
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/products_master",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Products Master"
                            className="dark:text-gray-300"
                          >
                            <Link to="/products_master">
                              <SidebarMenuButton
                                icon={<GraduationCap size={20} />}
                                className={activeClass("/products_master")}
                              >
                                Products Master
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/products_stock",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Products Stock"
                            className="dark:text-gray-300"
                          >
                            <Link to="/products_stock">
                              <SidebarMenuButton
                                icon={<Database size={20} />}
                                className={activeClass("/products_stock")}
                              >
                                Products Stock
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/rejected_orders",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Rejected Orders"
                            className="dark:text-gray-300"
                          >
                            <Link to="/rejected_orders">
                              <SidebarMenuButton
                                icon={<XCircle size={20} />}
                                className={activeClass("/rejected_orders")}
                              >
                                Rejected Orders
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}

                        {canAccessRoute(
                          userRole,
                          "/non_operational_days",
                          routePermissions,
                        ) && (
                          <SidebarMenuItem
                            title="Non Operational Days"
                            className="dark:text-gray-300"
                          >
                            <Link to="/non_operational_days">
                              <SidebarMenuButton
                                icon={<XCircle size={20} />}
                                className={activeClass("/non_operational_days")}
                              >
                                Non Operational Days
                              </SidebarMenuButton>
                            </Link>
                          </SidebarMenuItem>
                        )}
                      </SidebarGroup>
                    </>
                  )}
                </>
              )}
            </SidebarMenu>
          </SidebarContent>
        </div>

        {/* ── Fixed bottom section: Tech/Admin (non-provider only) ── */}
        {!isProvider && (
          <div className="pb-20 flex-shrink-0">
            <SidebarDivider />
            {(canAccessRoute(userRole, "/changelogs", routePermissions) ||
              canAccessRoute(userRole, "/audit_logs", routePermissions) ||
              canAccessRoute(userRole, "/queues", routePermissions)) && (
              <SidebarGroup
                title="Tech/Admin"
                icon={<Settings2 size={20} />}
                defaultOpen={isInGroup("tech")}
              >
                {canAccessRoute(userRole, "/audit_logs", routePermissions) && (
                  <SidebarMenuItem
                    title="Audit Logs"
                    className="dark:text-gray-300"
                  >
                    <Link to="/audit_logs">
                      <SidebarMenuButton
                        icon={<ClipboardList size={20} />}
                        className={activeClass("/audit_logs")}
                      >
                        Audit Logs
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                )}

                {canAccessRoute(userRole, "/queues", routePermissions) && (
                  <SidebarMenuItem
                    title="Queues"
                    className="dark:text-gray-300"
                  >
                    <Link to="/queues">
                      <SidebarMenuButton
                        icon={<ScrollText size={20} />}
                        className={activeClass("/queues")}
                      >
                        Queues
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                )}
              </SidebarGroup>
            )}

            {/* Theme toggle always visible for non-provider users */}
            <SidebarGroup title="Theme" icon={<Moon size={20} />}>
              <SidebarMenuItem
                title="Toggle theme"
                className="dark:text-gray-300"
              >
                <SidebarMenuButton
                  icon={
                    theme === "dark" ? (
                      <Moon size={20} className="text-gray-300" />
                    ) : (
                      <Sun size={20} />
                    )
                  }
                  className="dark:hover:bg-gray-800"
                  onClick={handleThemeChange}
                >
                  {theme === "dark" ? "Dark Mode" : "Light Mode"}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarGroup>
          </div>
        )}
      </ShadcnSidebar>
      <SidebarTrigger className="dark:bg-gray-900 dark:text-white dark:border-gray-700 dark:hover:bg-gray-800" />
    </>
  );
};
